import { Box, Button, Fade, Stack, Typography } from '@mui/material';
import { theme } from '@src/theme';
import { ErrorCode, useDropzone } from 'react-dropzone';
import { ReactComponent as FileIcon } from '@icons/file.svg';
import { ReactComponent as FilePlusIcon } from '@icons/file_plus.svg';
import { useCallback, useEffect, useState } from 'react';
import { buildDataTestIdAttribute } from '@src/utils';

const styles = {
  wrapper: {
    cursor: 'pointer',
    width: '100%',
    maxWidth: '234px',
    height: '141px',
    backgroundColor: 'transparent',
    transition: 'background-color 0.5s',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '13px 18px',
  },
};

const MAX_FILE_SIZE = 1_048_576;
export const DropFiles = ({ setValue, dataTestId }) => {
  const [file, setFile] = useState('');
  const [hoverDropZone, setHoverDropzone] = useState(false);
  const [error, setError] = useState('');

  const onDrop = useCallback(
    (files: File[]) => {
      setHoverDropzone(false);
      /*  if (!rejections) { */
      setFile(URL.createObjectURL(files[0]));
      setValue('attachmentFile', files[0]);
    },
    [setValue],
  );

  const { acceptedFiles, getRootProps, getInputProps, fileRejections } = useDropzone({
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg'],
      'omage/jpg': ['.jpg'],
    },
    onDrop,
  });

  useEffect(() => {
    if (!fileRejections || fileRejections.length === 0) setError('');
    fileRejections[0]?.errors?.forEach(({ code }) => {
      if (code === ErrorCode.TooManyFiles) {
        setError('You may add no more than 1 file.');
      }
      if (code === ErrorCode.FileInvalidType) {
        setError('Invalid file type (.jpeg, .jpg, .png).');
      }
      if (code === ErrorCode.FileTooLarge) {
        setError('Allowed attachment’s size exceeded (1MB).');
      }
    });
  }, [fileRejections]);

  const onHover = () => setHoverDropzone(true);
  const onBlur = () => setHoverDropzone(false);

  const deleteFile = () => {
    acceptedFiles.pop();
    setFile('');
    setValue('attachmentFile', '');
  };

  const fileIsUpload = acceptedFiles.length === 1;

  return (
    <>
      <Stack direction="row" gap="12px" alignItems="start">
        <Box width="234px" height="141px" bgcolor={theme.palette.grey[200]} position="relative">
          <Fade in={fileIsUpload}>
            <img
              width="234px"
              height="141px"
              alt="b2c_image"
              src={file || ''}
              onLoad={() => {
                URL.revokeObjectURL(file);
              }}
              style={{ objectFit: 'cover', position: 'absolute', zIndex: 10 }}
            />
          </Fade>

          <div
            {...getRootProps({ className: 'dropzone' })}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%' }}
          >
            <input {...getInputProps()} {...buildDataTestIdAttribute(dataTestId, 'input-file')} />

            <Stack
              sx={{
                ...styles.wrapper,
                border: `1px dashed ${fileIsUpload ? '#FFF' : theme.palette.grey[300]}`,
                '&:hover': {
                  backgroundColor: fileIsUpload ? 'transparent' : theme.palette.grey[300],
                  border: fileIsUpload ? 'inherit' : `1px dashed ${theme.palette.grey[400]}`,
                },
                '&:acive': {
                  backgroundColor: fileIsUpload ? 'transparent' : theme.palette.grey[400],
                  border: fileIsUpload ? 'inherit' : `1px dashed ${theme.palette.grey[500]}`,
                },
              }}
              onMouseEnter={onHover}
              onMouseLeave={onBlur}
            >
              {hoverDropZone ? (
                <Fade in={hoverDropZone}>
                  <Box sx={{ 'svg path': { fill: fileIsUpload ? '#FFFFFF' : theme.palette.grey[400] } }}>
                    {' '}
                    <FilePlusIcon />
                  </Box>
                </Fade>
              ) : (
                <Box>
                  <Stack
                    padding="9px"
                    alignItems="center"
                    sx={{ 'svg path': { fill: fileIsUpload ? '#FFFFFF' : theme.palette.primary.main } }}
                  >
                    <FileIcon />
                  </Stack>
                  <Typography
                    color={fileIsUpload ? '#FFFFFF' : 'grey.400'}
                    fontSize="12px"
                    lineHeight="16px"
                    fontWeight={600}
                    textAlign="center"
                  >
                    Drop the image or click to upload
                  </Typography>
                  <Typography
                    color={fileIsUpload ? '#FFFFFF' : 'grey.400'}
                    fontSize="12px"
                    lineHeight="16px"
                    fontWeight={500}
                    textAlign="center"
                  >
                    File size must not exceed 1 MB. Only JPG, PNG formats are allowed.
                  </Typography>
                </Box>
              )}
            </Stack>
          </div>
        </Box>
        <Fade in={fileIsUpload}>
          <Button
            variant="outlined"
            color="primary"
            sx={{ height: '30px' }}
            onClick={deleteFile}
            {...buildDataTestIdAttribute(dataTestId, 'input-file_button-delete')}
          >
            Delete
          </Button>
        </Fade>
      </Stack>
      {error && (
        <Typography component="p" color="primary.main" variant="label12Medium" mt="3px" lineHeight="16px">
          {error}
        </Typography>
      )}
    </>
  );
};
